import { CommonModule, DecimalPipe } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { NgModule } from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import {
  MatBottomSheetModule,
  MatCardModule,
  MatCheckboxModule,
  MatDialogModule,
  MatDividerModule,
  MatExpansionModule,
  MatFormFieldModule,
  MatIconModule,
  MatInputModule,
  MatListModule,
  MatMenuModule,
  MatPaginatorIntl,
  MatPaginatorModule,
  MatProgressSpinnerModule,
  MatSelectModule,
  MatSnackBarModule,
  MatTableModule,
  MatTooltipModule,
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
} from '@angular/material'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButtonToggleModule } from '@angular/material/button-toggle'
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { AlertComponent } from './alert/alert.component'
import { BreadcrumbPageComponent } from './breadcrumb-page/breadcrumb-page.component'
import { BreadcrumbComponent } from './breadcrumb/breadcrumb.component'
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component'
import { DashboardCardComponent } from './dashboard-card/dashboard-card.component'
import { AlphaNumericDirective } from './directives/alpha-numeric.directive'
import { CpfCnpjDirective } from './directives/cpf-cnpj/cpf-cnpj.directive'
import { OnlyNumbersDirective } from './directives/only-numbers.directive'
import { ValueToPlaceholderDirective } from './directives/valueToPlaceholder/value-to-placeholder.directive'
import { ErrorLoginComponent } from './error-login/error-login.component'
import { FileUploadComponent } from './file-upload/file-upload.component'
import { FooterLinkComponent } from './footer/footer-link/footer-link.component'
import { FooterComponent } from './footer/footer.component'
import { ContextButtonComponent } from './header/context-button/context-button.component'
import { ContextConfigurationComponent } from './header/context-configuration/context-configuration.component'
import { ContextComponent } from './header/context/context.component'
import { HeaderContainerComponent } from './header/header-container/header-container.component'
import { BurgerMenuComponent } from './header/menu/burger-menu/burger-menu.component'
import { BurgerModalOptionComponent } from './header/menu/burger-menu/burger-modal-option/burger-modal-option.component'
import { MenuModule } from './header/menu/menu.module'
import { UserPersonalInformationComponent } from './header/user-personal-information/user-personal-information.component'
import { UserProfileContainerComponent } from './header/user-profile-container/user-profile-container.component'
import { UserProfileComponent } from './header/user-profile/user-profile.component'
import { ItauModalComponent } from './itau-modal/itau-modal.component'
import { LoadingComponent } from './loading/loading.component'
import { PaginatorI18n } from './paginator/paginator-i18n.service'
import { CamelCaseToSpacePipe } from './pipes/camel-case-to-space.pipe'
import { DocumentPersonPipe } from './pipes/document-person.pipe'
import { FormatDatePipe } from './pipes/format-date.pipe'
import { OccurrenceTableFormatNamePipe } from './pipes/occurrence-table-format-name.pipe'
import { ReplaceLineBreakPipe } from './pipes/replace-line-break.pipe'
import { ReplacePipe } from './pipes/replace.pipe'
import { SearchBarComponent } from './search/search-bar/search-bar.component'
import { SearchResultItemComponent } from './search/search-result-item/search-result-item.component'
import { SortIconComponent } from './sort-icon/sort-icon.component'
import { TablePaginationComponent } from './table-pagination/table-pagination.component'

// AoT requires an exported function for factories
/*
export function HttpLoaderFactory(httpClient: HttpClient) {
    return new TranslateHttpLoader(httpClient);
} */

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    './assets/i18n/', // or whatever path you're using
    '.json'
  )
}
@NgModule({
  declarations: [
    AlertComponent,
    BreadcrumbComponent,
    BurgerMenuComponent,
    BurgerModalOptionComponent,
    ContextConfigurationComponent,
    FooterComponent,
    FooterLinkComponent,
    HeaderContainerComponent,
    ItauModalComponent,
    SearchBarComponent,
    SearchResultItemComponent,
    UserProfileComponent,
    UserPersonalInformationComponent,
    ContextComponent,
    UserProfileContainerComponent,
    ContextButtonComponent,
    LoadingComponent,
    ErrorLoginComponent,
    FileUploadComponent,
    ConfirmDialogComponent,
    OccurrenceTableFormatNamePipe,
    ReplaceLineBreakPipe,
    ReplacePipe,
    CpfCnpjDirective,
    BreadcrumbPageComponent,
    DocumentPersonPipe,
    DashboardCardComponent,
    SortIconComponent,
    TablePaginationComponent,
    FormatDatePipe,
    CamelCaseToSpacePipe,
    AlphaNumericDirective,
    OnlyNumbersDirective,
    ValueToPlaceholderDirective,
  ],
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    MatButtonToggleModule,
    MatDialogModule,
    MatCheckboxModule,
    MatDividerModule,
    MatExpansionModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatDividerModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatSnackBarModule,
    MatBottomSheetModule,
    MenuModule,
    ReactiveFormsModule,
    MatTooltipModule,
    MatCardModule,
    MatSelectModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  entryComponents: [AlertComponent, ConfirmDialogComponent],
  exports: [
    AlertComponent,
    BreadcrumbComponent,
    BurgerMenuComponent,
    ContextConfigurationComponent,
    FooterComponent,
    HeaderContainerComponent,
    ItauModalComponent,
    MatDividerModule,
    MatCheckboxModule,
    LoadingComponent,
    SearchBarComponent,
    SearchResultItemComponent,
    UserPersonalInformationComponent,
    UserProfileComponent,
    ErrorLoginComponent,
    FileUploadComponent,
    ConfirmDialogComponent,
    OccurrenceTableFormatNamePipe,
    ReplaceLineBreakPipe,
    ReplacePipe,
    CpfCnpjDirective,
    BreadcrumbPageComponent,
    DocumentPersonPipe,
    DashboardCardComponent,
    DecimalPipe,
    SortIconComponent,
    TablePaginationComponent,
    FormatDatePipe,
    CamelCaseToSpacePipe,
    AlphaNumericDirective,
    OnlyNumbersDirective,
    MatInputModule,
    ValueToPlaceholderDirective,
  ],
  providers: [
    {
      provide: MatPaginatorIntl,
      deps: [TranslateService],
      useClass: PaginatorI18n,
    },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 10000 } },
    DecimalPipe,
  ],
})
export class SharedModule {}
